import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { CurrencyToggle, useAdminCurrency } from '../../components/admin/CurrencyToggle';
import { StatusBadge, STATUS_PAYMENT } from '../../components/admin/StatusBadge';

interface Payment {
  id: string; amount: number; status: string; product_price_id: string;
  created_at: string; user_id: string; stripe_fee?: number | null;
  profiles: { email: string | null; full_name: string | null } | null;
  product_prices: { name: string | null } | null;
}

type StatusFilter = 'all' | 'succeeded' | 'failed' | 'refunded';

export function AdminPaymentsPage() {
  const { fmt }              = useAdminCurrency();
  const [payments,  setPayments]  = useState<Payment[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [status,    setStatus]    = useState<StatusFilter>('all');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [totalRev,  setTotalRev]  = useState(0);
  const [totalFee,  setTotalFee]  = useState(0);

  useEffect(() => { load(); }, [status, dateFrom, dateTo]);

  async function load() {
    setLoading(true);
    let q = supabase
      .from('payments')
      .select('id, amount, status, product_price_id, created_at, user_id, profiles:user_id(email, full_name), product_prices:product_price_id(name)')
      .order('created_at', { ascending: false })
      .limit(200);

    if (status !== 'all') q = q.eq('status', status);
    if (dateFrom) q = q.gte('created_at', dateFrom + 'T00:00:00Z');
    if (dateTo)   q = q.lte('created_at', dateTo + 'T23:59:59Z');

    const { data } = await q;
    const rows = (data ?? []) as unknown as Payment[];
    setPayments(rows);
    const succeeded = rows.filter(p => p.status === 'succeeded');
    setTotalRev(succeeded.reduce((s, p) => s + (p.amount ?? 0), 0));
    setTotalFee(succeeded.reduce((s, p) => s + Math.round((p.amount ?? 0) * 0.0299 + 39), 0));
    setLoading(false);
  }

  function exportCsv() {
    const headers = ['Data', 'Usuário', 'Produto', 'Valor', 'Status'];
    const rows = payments.map(p => [
      new Date(p.created_at).toLocaleDateString('pt-BR'),
      p.profiles?.email ?? p.user_id,
      p.product_prices?.name ?? p.product_price_id,
      String(p.amount),
      p.status,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `pagamentos_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
    succeeded: { bg: 'rgba(74,222,128,0.12)',  color: '#4ade80',  label: 'Pago' },
    failed:    { bg: 'rgba(239,68,68,0.1)',    color: '#fca5a5',  label: 'Falhou' },
    refunded:  { bg: 'rgba(251,191,36,0.1)',   color: '#fbbf24',  label: 'Reembolsado' },
    pending:   { bg: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', label: 'Pendente' },
  };

  return (
    <div>
      <AdminPageHeader
        title="Pagamentos"
        objective="Histórico de todas as transações financeiras: compras aprovadas, falhas de cobrança e reembolsos. Use para conciliar receita e investigar problemas. Exporte para CSV para uso em planilhas."
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          {!loading && (
            <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
              {payments.length} registros · Bruto: <strong style={{ color: '#4ade80' }}>{fmt(totalRev)}</strong>
              {' '}· Taxa estimada: <strong style={{ color: '#f87171' }}>{fmt(totalFee)}</strong>
              {' '}· Líquido: <strong style={{ color: '#F5C842' }}>{fmt(totalRev - totalFee)}</strong>
            </p>
          )}
          <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Taxa Stripe = estimativa de 2,99% + R$0,39. Valores exatos disponíveis no painel Stripe.
          </p>
          {payments.length >= 200 && (
            <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-caption)', color: '#fbbf24' }}>
              Exibindo apenas os 200 registros mais recentes. Use os filtros de data para ver períodos específicos.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <CurrencyToggle />
          <button className="btn btn-secondary btn-sm" onClick={exportCsv}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Download size={14} /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem', alignItems: 'center' }}>
        {(['all', 'succeeded', 'failed', 'refunded'] as StatusFilter[]).map(s => (
          <button key={s} onClick={() => setStatus(s)}
            className={status === s ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
            {s === 'all' ? 'Todos' : STATUS_STYLE[s]?.label ?? s}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <input type="date" className="form-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ maxWidth: '155px' }} />
          <input type="date" className="form-input" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ maxWidth: '155px' }} />
          {(dateFrom || dateTo) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>✕</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>Data</th><th>Usuário</th><th>Produto</th><th>Bruto</th><th>Taxa</th><th>Líquido</th><th>Moeda</th><th>País</th><th>Status</th></tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id}>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(p.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td>
                    <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600 }}>{p.profiles?.full_name ?? '-'}</p>
                    <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>{p.profiles?.email ?? p.user_id}</p>
                  </td>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{p.product_prices?.name ?? p.product_price_id}</td>
                  <td style={{ fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{fmt(p.amount)}</td>
                  <td style={{ fontSize: 'var(--text-caption)', color: '#f87171' }}>
                    {p.status === 'succeeded' ? fmt(Math.round((p.amount ?? 0) * 0.0299 + 39)) : '-'}
                  </td>
                  <td style={{ fontWeight: 600, color: '#F5C842' }}>
                    {p.status === 'succeeded' ? fmt((p.amount ?? 0) - Math.round((p.amount ?? 0) * 0.0299 + 39)) : '-'}
                  </td>
                  <td>
                    <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                      BRL
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                      BR
                    </span>
                  </td>
                  <td>
                    <StatusBadge {...(STATUS_PAYMENT[p.status] ?? STATUS_PAYMENT.pending)} />
                  </td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                  Nenhum pagamento encontrado
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
